import { useRef, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuthTenant } from 'store/auth';
import styled from 'styled-components';

import { getAreas } from 'services/areas-service';
import { getAssets } from 'services/assets-service';
import { getSystems } from 'services/systems-service';
import { getProcesses } from 'services/processes-service';

import { Main } from '../../Components/Main';
import Graphic from './Graphic';
import { PageHelper } from '../../Components/PageHelper';
import { Spinner } from 'Components/common/Spinner';
import { PageHeader } from 'Components/ui';

const MinusSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PlusSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const Map = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const { t } = useTranslation(['mapping', 'common']);

  const tourSteps = useMemo(() => [
    {
      target: 'body',
      content: t('mapping:tour.welcome'),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="stats-cards"]',
      content: t('mapping:tour.statsCards'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="map-visualization"]',
      content: t('mapping:tour.visualization'),
    },
    {
      target: '[data-tour="details-tables"]',
      content: t('mapping:tour.details'),
    },
    {
      target: 'body',
      content: t('mapping:tour.done'),
      placement: 'center',
    },
  ], [t]);

  const { data: companyAreas, isLoading: areasLoading } = useQuery(['getAreas'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getAreas(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companyProcesses, isLoading: processesLoading } = useQuery(['getProcesses'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getProcesses(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companySystems, isLoading: systemsLoading } = useQuery(['getSystems'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getSystems(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const { data: companyAssets, isLoading: assetsLoading } = useQuery(['getAssets'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getAssets(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const graphicRef   = useRef(null);
  const [hasSelection, setHasSelection] = useState(false);

  const areasCount = companyAreas?.length || 0;
  const processesCount = companyProcesses?.length || 0;
  const systemsCount = companySystems?.length || 0;
  const assetsCount = companyAssets?.length || 0;

  const isLoading = areasLoading || processesLoading || systemsLoading || assetsLoading;

  return (
    <>
      <PageHelper steps={tourSteps} tourKey="map-tour" autoStart={true} showButton={true} />
      <Main>
        <PageWrapper>
          {/* Page header */}
          <PageHeader
            title={t('mapping:page.title')}
            segments={[
              { label: t('common:breadcrumbs.mapping') },
              { label: t('mapping:page.title') },
            ]}
          />

          {/* Stats */}
          <StatsGrid data-tour="stats-cards">
            <StatCard $color="var(--vo-cobalto)" $shadow="color-mix(in srgb, var(--vo-cobalto) 15%, transparent)">
              <StatNumber $color="var(--vo-cobalto)">
                {areasLoading ? <Spinner $size={28} /> : areasCount}
              </StatNumber>
              <StatLabel>{t('mapping:stats.areas')}</StatLabel>
            </StatCard>

            <StatCard $color="var(--vo-purple)" $shadow="color-mix(in srgb, var(--vo-purple) 15%, transparent)">
              <StatNumber $color="var(--vo-purple)">
                {processesLoading ? <Spinner $size={28} /> : processesCount}
              </StatNumber>
              <StatLabel>{t('mapping:stats.processes')}</StatLabel>
            </StatCard>

            <StatCard $color="var(--vo-celeste)" $shadow="var(--vo-info-15)">
              <StatNumber $color="var(--vo-celeste)">
                {systemsLoading ? <Spinner $size={28} /> : systemsCount}
              </StatNumber>
              <StatLabel>{t('mapping:stats.systems')}</StatLabel>
            </StatCard>

            <StatCard $color="var(--vo-success)" $shadow="var(--vo-success-15)">
              <StatNumber $color="var(--vo-success)">
                {assetsLoading ? <Spinner $size={28} /> : assetsCount}
              </StatNumber>
              <StatLabel>{t('mapping:stats.assets')}</StatLabel>
            </StatCard>
          </StatsGrid>

          {/* Organogram card */}
          <OrgCard data-tour="map-visualization">
            <OrgCardHdr>
              <div>
                <OrgTitle>{t('mapping:visualization.orgTitle')}</OrgTitle>
                <OrgSub>{t('mapping:visualization.orgSubtitle')}</OrgSub>
              </div>
              <CtrlGroup>
                {hasSelection && (
                  <CtrlBtn type="button" onClick={() => { graphicRef.current?.clearSelection(); setHasSelection(false); }}>
                    {t('mapping:visualization.clearSelection')}
                  </CtrlBtn>
                )}
                <CtrlBtn type="button" onClick={() => graphicRef.current?.collapseAll()}><MinusSvg />{t('mapping:visualization.collapseAll')}</CtrlBtn>
                <CtrlBtnPrimary type="button" onClick={() => graphicRef.current?.expandAll()}><PlusSvg />{t('mapping:visualization.expandAll')}</CtrlBtnPrimary>
                <CtrlDivider />
                <ZoomBtn type="button" onClick={() => graphicRef.current?.zoomOut()}>−</ZoomBtn>
                <ZoomBtnPrimary type="button" onClick={() => graphicRef.current?.zoomIn()}>+</ZoomBtnPrimary>
              </CtrlGroup>
            </OrgCardHdr>

            <OrgScrollArea>
              {isLoading ? (
                <LoadingWrap>
                  <Spinner $size={60} />
                </LoadingWrap>
              ) : (
                !isLoading && (
                  <Graphic
                    ref={graphicRef}
                    onSelectionChange={(id) => setHasSelection(!!id)}
                    areas={companyAreas}
                    processes={companyProcesses}
                    systems={companySystems}
                    assets={companyAssets}
                  />
                )
              )}
            </OrgScrollArea>
          </OrgCard>

          {/* Detail cards */}
          <DetailGrid data-tour="details-tables">
            {/* Areas */}
            <DetailCardWrap>
              <DetailHdr>
                <DetailDot $grad="linear-gradient(135deg,var(--vo-cobalto),var(--vo-celeste))" />
                <DetailTitle $color="var(--vo-cobalto)">{t('mapping:sections.areas', { count: areasCount })}</DetailTitle>
              </DetailHdr>
              <EntityList>
                {companyAreas?.length > 0 ? (
                  companyAreas.map(area => (
                    <EntityItem key={area._id}>
                      <EntityName>{area.name}</EntityName>
                      <EntityMeta>
                        {t('mapping:fields.responsible')} {area.user || '—'} · {t('mapping:fields.criticity')} {area.criticity || '—'} · {t('mapping:fields.processes')} {area.processes?.length || 0} · {t('mapping:fields.systems')} {area.systems?.length || 0}
                      </EntityMeta>
                    </EntityItem>
                  ))
                ) : (
                  <EntityEmpty>{t('mapping:empty.areas')}</EntityEmpty>
                )}
              </EntityList>
            </DetailCardWrap>

            {/* Processes */}
            <DetailCardWrap>
              <DetailHdr>
                <DetailDot $grad="linear-gradient(135deg,var(--vo-purple),color-mix(in srgb, var(--vo-purple) 60%, white))" />
                <DetailTitle $color="var(--vo-purple)">{t('mapping:sections.processes', { count: processesCount })}</DetailTitle>
              </DetailHdr>
              <EntityList>
                {companyProcesses?.length > 0 ? (
                  companyProcesses.map(process => (
                    <EntityItem key={process._id}>
                      <EntityName>{process.name}</EntityName>
                      <EntityMeta>
                        {t('mapping:fields.responsible')} {process.user || '—'} · {t('mapping:fields.criticity')} {process.criticity || '—'} · {t('mapping:fields.dpia')} {process.dpia ? t('common:boolean.yes') : t('common:boolean.no')} · {t('mapping:fields.data')} {process.dataMapping?.length || 0}
                      </EntityMeta>
                    </EntityItem>
                  ))
                ) : (
                  <EntityEmpty>{t('mapping:empty.processes')}</EntityEmpty>
                )}
              </EntityList>
            </DetailCardWrap>

            {/* Systems */}
            <DetailCardWrap>
              <DetailHdr>
                <DetailDot $grad="linear-gradient(135deg,var(--vo-celeste),var(--vo-capri))" />
                <DetailTitle $color="var(--vo-celeste)">{t('mapping:sections.systems', { count: systemsCount })}</DetailTitle>
              </DetailHdr>
              <EntityList>
                {companySystems?.length > 0 ? (
                  companySystems.map(system => (
                    <EntityItem key={system._id}>
                      <EntityName>{system.name}</EntityName>
                      <EntityMeta>
                        {t('mapping:fields.saas')} {system.saas ? t('common:boolean.yes') : t('common:boolean.no')} · {t('mapping:fields.developedInHouse')} {system.developedInHouse ? t('common:boolean.yes') : t('common:boolean.no')} · {t('mapping:fields.criticity')} {system.criticity || '—'}
                      </EntityMeta>
                    </EntityItem>
                  ))
                ) : (
                  <EntityEmpty>{t('mapping:empty.systems')}</EntityEmpty>
                )}
              </EntityList>
            </DetailCardWrap>

            {/* Assets */}
            <DetailCardWrap>
              <DetailHdr>
                <DetailDot $grad="linear-gradient(135deg,var(--vo-success),color-mix(in srgb, var(--vo-success) 80%, white))" />
                <DetailTitle $color="var(--vo-success)">{t('mapping:sections.assets', { count: assetsCount })}</DetailTitle>
              </DetailHdr>
              <EntityList>
                {companyAssets?.length > 0 ? (
                  companyAssets.map(asset => (
                    <EntityItem key={asset._id}>
                      <EntityName>{asset.name}</EntityName>
                      <EntityMeta>
                        {t('mapping:fields.type')} {asset.type || '—'} · {t('mapping:fields.location')} {asset.location || '—'} · {t('mapping:fields.criticity')} {asset.criticity || '—'}
                      </EntityMeta>
                    </EntityItem>
                  ))
                ) : (
                  <EntityEmpty>{t('mapping:empty.assets')}</EntityEmpty>
                )}
              </EntityList>
            </DetailCardWrap>
          </DetailGrid>
        </PageWrapper>
      </Main>
    </>
  );
};

// ── Styled components ─────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  padding: 24px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  display: flex;
  align-items: center;
  gap: 14px;
  border-left: 4px solid ${p => p.$color};
  transition: box-shadow .15s;
  &:hover { box-shadow: 0 4px 12px ${p => p.$shadow}; }
`;

const StatNumber = styled.div`
  font-family: 'Rubik', sans-serif;
  font-size: 36px;
  font-weight: 800;
  color: ${p => p.$color};
  letter-spacing: -.03em;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
`;

const StatLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-slate);
  margin: 0;
  line-height: 1.4;
  white-space: pre-line;
`;

const OrgCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
  margin-bottom: 20px;
`;

const OrgCardHdr = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid var(--vo-cloud);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const OrgTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;

const OrgSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

const CtrlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CtrlBtn = styled.button`
  height: 32px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  &:hover { background: var(--vo-mist); }
`;

const CtrlBtnPrimary = styled(CtrlBtn)`
  background: var(--vo-capri-50);
  color: var(--vo-celeste);
  border-color: var(--vo-line);
  font-weight: 600;
  &:hover { background: var(--vo-capri-100); }
`;

const CtrlDivider = styled.div`
  width: 1px;
  height: 24px;
  background: var(--vo-cloud);
  flex-shrink: 0;
`;

const ZoomBtn = styled.button`
  width: 32px;
  height: 32px;
  background: white;
  border: 1.5px solid var(--vo-line);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vo-slate);
  font-size: 16px;
  font-weight: 700;
  &:hover { background: var(--vo-mist); }
`;

const ZoomBtnPrimary = styled(ZoomBtn)`
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-celeste));
  border: none;
  color: white;
  &:hover { background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-celeste)); opacity: 1; }
`;

const OrgScrollArea = styled.div`
  overflow: auto;
  padding: 24px;
  background: var(--vo-capri-25);
  min-height: 600px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 600px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DetailCardWrap = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const DetailHdr = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const DetailDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${p => p.$grad};
  flex-shrink: 0;
`;

const DetailTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${p => p.$color};
  margin: 0;
`;

const EntityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 360px;
  overflow: auto;
`;

const EntityItem = styled.div`
  padding: 12px 14px;
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
`;

const EntityName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 3px;
  word-break: break-word;
`;

const EntityMeta = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  color: var(--vo-steel);
  margin: 0;
  word-break: break-word;
`;

const EntityEmpty = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  text-align: center;
  padding: 16px 0;
`;
